import { useFormikContext } from 'formik';
import { useState } from 'react';

import { Callout } from '../../../cdl/Callout/Callout';
import { StatePlaceholder } from '../../../cdl/StatePlaceholder/StatePlaceholder';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconGameplan } from '../../../common/icons/cdl/Gameplan';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { Box } from '../../../common/ui/Box';
import { getDemandCategorySummaries } from '../../getDemandCategorySummaries';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandProductCategory } from '../../types/DemandProductCategory';
import { DemandCardSection } from '../DemandCardSection';
import { DemandHistoricalEquivalents } from '../DemandHistoricalEquivalents';
import { DemandHistoricalTotalCard } from '../DemandHistoricalTotalCard';
import { DemandMetricCard } from '../DemandMetricCard';
import { DemandReferencePeriodValue } from '../DemandReferencePeriodValue';

export const HistoricalDemandStep = () => {
    const [selectedCategory, setSelectedCategory] = useState<DemandProductCategory>();
    const { calculation, retainedCalculation, categories, companies, isCalculationError, isCalculationLoading } =
        useDemandDefinitionCalculationContext();
    const { values } = useFormikContext<DemandDefinitionState>();
    const { referencePeriodFrom, referencePeriodTo } = values;
    const displayedCalculation = calculation ?? retainedCalculation;
    const historicalCategories = getDemandCategorySummaries(displayedCalculation, 'historicalVolume');
    const selectedCompanyCount = companies.filter((company) => !values.excludedCompanyIds.includes(company.id)).length;
    const categoryLabels = new Map(categories.map((category) => [category.id, category.label]));
    const selectedCategoryData = historicalCategories.find((category) => category.category === selectedCategory);

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            {isCalculationError ? (
                <Callout
                    variant="negative"
                    heading={translate('demandDefinition.calculationFailed')}
                    description={translate('demandDefinition.calculationFailedDescription')}
                />
            ) : null}

            {displayedCalculation || isCalculationLoading ? (
                <DemandCardSection columns={2} isLoading={isCalculationLoading}>
                    <Box gridColumn="1 / -1">
                        <DemandMetricCard
                            label={translate('demandDefinition.referencePeriod')}
                            value={<DemandReferencePeriodValue from={referencePeriodFrom} to={referencePeriodTo} />}
                        />
                    </Box>
                    <DemandMetricCard
                        label={translate('demandDefinition.includedCompanies')}
                        value={
                            displayedCalculation
                                ? formatNumber({ number: selectedCompanyCount, maximumFractionDigits: 0 })
                                : undefined
                        }
                    />
                    <DemandMetricCard
                        label={translate('demandDefinition.includedVessels')}
                        value={
                            displayedCalculation
                                ? formatNumber({
                                      number: displayedCalculation.total.vesselCount,
                                      maximumFractionDigits: 0,
                                  })
                                : undefined
                        }
                    />
                </DemandCardSection>
            ) : null}

            {displayedCalculation && historicalCategories.length > 0 ? (
                <DemandHistoricalTotalCard
                    categories={historicalCategories}
                    categoryLabels={categoryLabels}
                    totalVolume={displayedCalculation.total.historicalVolume}
                    selectedCategory={selectedCategoryData?.category}
                    onSelectCategory={setSelectedCategory}
                />
            ) : null}

            {displayedCalculation && selectedCategoryData ? (
                <DemandHistoricalEquivalents
                    key={selectedCategoryData.category}
                    calculation={displayedCalculation}
                    category={selectedCategoryData.category}
                    categoryLabel={categoryLabels.get(selectedCategoryData.category) ?? selectedCategoryData.category}
                />
            ) : null}

            {displayedCalculation && historicalCategories.length > 0 && !selectedCategoryData ? (
                <Callout
                    Icon={IconInfoCircle}
                    description={translate('demandDefinition.clickOnAProductCategoryAboveToSeeItsEquivalentProducts')}
                />
            ) : null}

            {displayedCalculation && historicalCategories.length === 0 ? (
                <StatePlaceholder
                    title={translate('demandDefinition.noHistoricalDemand')}
                    description={translate('demandDefinition.noHistoricalDemandDescription')}
                    Icon={IconGameplan}
                />
            ) : null}
        </Box>
    );
};
